import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

const COLORS = ['#2e7d32', '#1976d2', '#6d4c41', '#ed6c02', '#8e24aa', '#455a64'];

const DeviceInventorySummary = () => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);

  useEffect(() => { fetchSummary(); }, []); // eslint-disable-line

  async function fetchSummary() {
    setLoading(true); setError('');
    try {
      const response = await apiFetch('/api/intune/device-inventory-summary');
      if (!response.ok) throw new Error('Failed to fetch device inventory summary');
      const data = await response.json();
      setSummary(data);
    }
    catch (e) {
        Sentry.captureException(e);
        setError(e.message); setSummary(null);
    }
    finally { setLoading(false); }
  }

  const periodLabel = useMemo(() => {
    if (!summary?.periodStartUtc) return '';
    try { return new Date(summary.periodStartUtc).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }); }
    catch (catchError) {
        Sentry.captureException(catchError);
        return '';
    }
  }, [summary]);

  // Calculate device compliance health score
  const healthScore = useMemo(() => {
    if (!summary || summary.previousMonthComplianceRate == null) return null;
    // Device compliance is straightforward - direct compliance percentage
    return Math.round(summary.previousMonthComplianceRate);
  }, [summary]);

  const getScoreInterpretation = (score) => {
    if (score == null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 95) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 90) return { text: 'Good', color: '#7cb342', icon: '✓' };
    if (score >= 80) return { text: 'Fair', color: '#ffa000', icon: '⚠' };
    if (score >= 70) return { text: 'Needs Attention', color: '#ff5722', icon: '⚠' };
    return { text: 'Critical', color: '#d32f2f', icon: '✗' };
  };

  const categoriesPieData = useMemo(() => summary?.deviceCategories ? Object.entries(summary.deviceCategories).map(([name, value]) => ({ name, value })) : [], [summary]);

  if (loading) return <HexagonLoader />;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;
  if (!summary) return null;

  const complianceData = Object.entries(summary.complianceStates || {}).map(([state, count]) => ({ state, count }));
  const scoreInterp = getScoreInterpretation(healthScore);
  const complianceRate = summary.previousMonthComplianceRate ?? 0;
  const compliantDevices = summary.previousMonthCompliant ?? 0;
  const nonCompliantDevices = summary.previousMonthNonCompliant ?? 0;
  const totalDevices = summary.totalDevices ?? 0;

  const llmProxyText = [
    `Device Compliance Health Score: ${healthScore}/100`,
    `Total devices: ${totalDevices}`,
    `Compliance: ${complianceRate.toFixed(1)}% (${compliantDevices} compliant / ${nonCompliantDevices} non-compliant)`,
    `Most recent check-in: ${summary.mostRecentCheckin ? new Date(summary.mostRecentCheckin).toLocaleString() : 'N/A'}`
  ].join('\n');

  return (
    <div className="dashboard-card group-wrapper device-inventory-summary">
      <div data-llm-proxy style={{ display:'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop: 0, marginBottom: 12 }}>Device Management & Compliance</h2>

      {/* Executive Summary Banner */}
      <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
        <div className="exec-banner-layout">
          <div className="exec-banner-score">
            <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
              {healthScore != null ? healthScore : '—'}
              <span className="exec-banner-suffix">/ 100</span>
            </div>
            <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
            <div className="exec-banner-sublabel">Device Compliance Health</div>
          </div>
          <div className="exec-banner-body">
            <div className="exec-banner-text">
              <strong>What this means:</strong> Your organization manages <strong>{totalDevices} devices</strong>, with <strong>{complianceRate.toFixed(0)}%</strong> meeting security policies in {periodLabel}.
              {complianceRate >= 95 && ' Device security is excellent.'}
              {complianceRate >= 90 && complianceRate < 95 && ' Device security is good with minor gaps.'}
              {complianceRate < 90 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> {nonCompliantDevices} non-compliant devices pose security risks - encryption, updates, or policy violations.</span>}
              {complianceRate < 80 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> Compliance is critically low - immediate remediation required.</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-row">
        <div className="dashboard-card metric-card" title="Total devices under IT management via Microsoft Intune">
          <div className="metric-label">Managed Devices</div>
          <div className="metric-value">{totalDevices}</div>
          <div className="metric-subtitle" style={{ marginTop: 4 }}>Under IT control</div>
        </div>
        <div className="dashboard-card metric-card" title="Percentage of devices meeting organizational security policies">
          <div className="metric-label">Compliance Rate</div>
          <div className="metric-value" style={{ color: complianceRate >= 95 ? '#2e7d32' : complianceRate >= 90 ? '#7cb342' : complianceRate >= 80 ? '#ffa000' : '#d32f2f' }}>
            {complianceRate.toFixed(0)}%
          </div>
          <div className="metric-subtitle" style={{ marginTop: 4 }}>
            {compliantDevices} compliant / {nonCompliantDevices} non-compliant
          </div>
        </div>
        <div className="dashboard-card metric-card metric-card--lg" title="Most recent device check-in to management system">
          <div className="metric-label">Latest Activity</div>
          <div className="metric-value--text" style={{ fontWeight: 700, marginTop: 4 }}>
            {summary.mostRecentCheckin ? new Date(summary.mostRecentCheckin).toLocaleString() : 'N/A'}
          </div>
          <div className="metric-subtitle" style={{ marginTop: 4 }}>Most recent check-in</div>
        </div>
      </div>

      {/* Charts */}
      <div className="dashboard-grid compact">
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Current Compliance Status</h4>
          <div className="chart-subtitle">Devices by policy compliance state</div>
          <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={200} minHeight={200}>
            <BarChart data={complianceData} layout="vertical" margin={{ left:16, right:8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis dataKey="state" type="category" width={110} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#388e3c" radius={[3,3,3,3]} isAnimationActive={!isPrintMode} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Device Types</h4>
          <div className="chart-subtitle">Distribution by device category</div>
          {categoriesPieData.length === 0 && <div style={{ fontSize:11, color:'#777', padding: 20, textAlign: 'center' }}>No category data available.</div>}
          {categoriesPieData.length > 0 && (
            <ResponsiveContainer width={isPrintMode ? 330 : "100%"} height={200} minHeight={200}>
              <PieChart>
                <Pie data={categoriesPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} label={(d) => `${d.name} (${d.value})`} isAnimationActive={!isPrintMode}>
                  {categoriesPieData.map((e,i)=><Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Active Devices Table */}
      <div className="dashboard-card">
        <h4 style={{ margin:'0 0 8px 0', fontSize:14 }}>Recent Device Activity</h4>
        <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>Devices that have checked in recently - indicates active management</div>
        <div style={{ overflowX:'auto' }}>
          <table className="data-table">
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ textAlign:'left', padding: '6px', fontSize: 11, width: '100%' }}>Primary User</th>
                <th style={{ padding: '6px', fontSize: 11, textAlign: 'left', whiteSpace: 'nowrap' }}>Operating System</th>
                <th style={{ padding: '6px', fontSize: 11, textAlign: 'left', whiteSpace: 'nowrap' }}>Category</th>
                <th style={{ padding: '6px', fontSize: 11, textAlign: 'left', whiteSpace: 'nowrap' }}>Compliance</th>
                <th style={{ padding: '6px', fontSize: 11, textAlign: 'right', whiteSpace: 'nowrap' }}>Last Check-in</th>
              </tr>
            </thead>
            <tbody>
              {(summary.topActiveDevices || []).map((d,i)=>
                <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ textAlign:'left', padding: '6px', fontWeight: 500 }}>{d.primaryUser || '-'}</td>
                  <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{d.operatingSystem || '-'}</td>
                  <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{d.deviceCategory || '-'}</td>
                  <td style={{ padding: '6px', whiteSpace: 'nowrap', color: d.complianceState === 'Compliant' ? '#2e7d32' : d.complianceState === 'NonCompliant' ? '#d32f2f' : '#666', fontWeight: 600 }}>
                    {d.complianceState || '-'}
                  </td>
                  <td style={{ padding: '6px', fontSize: 11, textAlign: 'right', whiteSpace: 'nowrap' }}>{d.lastCheckin ? new Date(d.lastCheckin).toLocaleString() : '-'}</td>
                </tr>
              )}
              {(!summary.topActiveDevices || summary.topActiveDevices.length === 0) && (
                <tr><td colSpan={5} style={{ textAlign:'center', fontStyle:'italic', color:'#777', padding: 12 }}>No recent device activity found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Explanation Section */}
      <div className="explanation-box">
        <strong>Understanding Device Management Metrics:</strong>
        <ul>
          <li><strong>Device Compliance Health:</strong> Direct measurement of devices meeting security policies. Target is 95%+ for strong governance.</li>
          <li><strong>Compliance Rate:</strong> Percentage meeting policies for encryption, updates, password complexity, etc. Non-compliant devices are security risks.</li>
          <li><strong>Managed Devices:</strong> Under IT control via Intune. Unmanaged devices (shadow IT) aren't visible here and pose unknown risks.</li>
          <li><strong>Compliance States:</strong> Compliant (meeting all policies), Non-Compliant (failing one or more), In Grace Period (recently non-compliant), or Unknown (not yet evaluated).</li>
          <li><strong>Device Categories:</strong> Desktops, laptops, tablets, mobile phones. Different categories may have different risk profiles and policy requirements.</li>
          <li><strong>Check-in Activity:</strong> Devices checking in regularly are actively managed. Long periods without check-ins may indicate lost/stolen devices or connectivity issues.</li>
        </ul>
        {nonCompliantDevices > 0 && (
          <div className="action-callout">
            <strong>Action Required:</strong> {nonCompliantDevices} non-compliant device{nonCompliantDevices > 1 ? 's' : ''} detected. Common issues include missing encryption, outdated OS, weak passwords, or unapproved apps. Review Intune compliance reports and remediate or retire non-compliant devices to reduce security exposure.
          </div>
        )}
      </div>

      <div className="data-source">
        Data Source: Microsoft Intune device inventory snapshot. Compliance calculated from devices syncing within {periodLabel} period. Check-in times shown in local timezone. Compliance policies include encryption, OS version, password requirements, and application restrictions.
      </div>
    </div>
  );
};

export default DeviceInventorySummary;
